import { useEffect, useState } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import { useAppState } from './hooks/useAppState';
import { setTokenGetter, getSubscription } from './lib/api';
import { getTheme } from './lib/theme';
import Layout from './components/Layout';
import LoginPage from './pages/LoginPage';
import PlanesPage from './pages/PlanesPage';
import RegistroCentroPage from './pages/RegistroCentroPage';
import OnboardingPage from './pages/OnboardingPage';
import DashboardPage from './pages/DashboardPage';
import TrabajadoresPage from './pages/TrabajadoresPage';
import CuestionariosPage from './pages/CuestionariosPage';
import AplicarCuestionarioPage from './pages/AplicarCuestionarioPage';
import ConsentimientoPage from './pages/ConsentimientoPage';
import FichaPage from './pages/FichaPage';
import ResultadosPage from './pages/ResultadosPage';
import PoliticaPage from './pages/PoliticaPage';
import ProgramaPage from './pages/ProgramaPage';
import EvidenciaPage from './pages/EvidenciaPage';
import EnviarLinksPage from './pages/EnviarLinksPage';
import PortalPage from './pages/PortalPage';

// Smart home: if empresa exists → dashboard, else → inicio
function SmartHome() {
  const { state } = useAppState();
  if (state.empresaId && state.centrosTrabajo?.length > 0) {
    return <Navigate to="/dashboard" replace />;
  }
  return <Navigate to="/onboarding" replace />;
}

function SubscriptionBanner() {
  const [subStatus, setSubStatus] = useState(null);
  const navigate = useNavigate();
  useEffect(() => {
    getSubscription().then(sub => {
      if (sub && sub.status && sub.status !== 'active') setSubStatus(sub.status);
    }).catch(() => {});
  }, []);
  if (!subStatus) return null;
  const msgs = {
    past_due: 'Tu pago no se pudo procesar. Actualiza tu metodo de pago para mantener el acceso.',
    canceled: 'Tu suscripcion fue cancelada. Renueva tu plan para seguir usando la plataforma.',
    unpaid: 'Tienes un pago pendiente. Actualiza tu metodo de pago.',
  };
  return (
    <div style={{ padding: '10px 20px', background: '#fef2f2', borderBottom: '1px solid #fecaca', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
      <span style={{ fontSize: '0.85rem', color: '#dc2626', fontWeight: 500 }}>
        {msgs[subStatus] || 'Hay un problema con tu suscripcion.'}
      </span>
      <button onClick={() => navigate('/planes')} style={{ padding: '6px 16px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 6, fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
        Renovar plan
      </button>
    </div>
  );
}

export default function App() {
  const { user, loading: authLoading, getToken } = useAuth();
  const { state } = useAppState();
  const theme = getTheme(state.darkMode);
  const location = useLocation();

  // Connect auth token to API client
  useEffect(() => {
    setTokenGetter(getToken);
  }, [getToken]);

  // ── Public portal (always accessible, no auth needed) ────
  if (location.pathname.startsWith('/portal/')) {
    return <Routes><Route path="/portal/:token" element={<PortalPage />} /></Routes>;
  }

  // Always wait for auth to finish
  if (authLoading) {
    return (
      <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc' }}>
        <div style={{ textAlign: 'center', color: '#64748b' }}>
          <div style={{ fontSize: '2rem', marginBottom: 12 }}>⏳</div>
          <div style={{ fontSize: '0.9rem' }}>Verificando sesión...</div>
        </div>
      </div>
    );
  }

  // ── Not logged in ───────────────────────────────────────
  if (!user) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  // Espera a que cargue empresa/centros antes de decidir a dónde ir —
  // sin esto, SmartHome/Layout deciden con datos vacíos (empresaId aún
  // null) y mandan a /onboarding por un instante aunque sí haya empresa,
  // causando un parpadeo onboarding→dashboard justo después de loguear o
  // de cualquier recarga completa (ej. al volver del pago de Stripe).
  if (state.cargando) {
    return (
      <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc' }}>
        <div style={{ textAlign: 'center', color: '#64748b' }}>
          <div style={{ fontSize: '2rem', marginBottom: 12 }}>⏳</div>
          <div style={{ fontSize: '0.9rem' }}>Cargando tu información...</div>
        </div>
      </div>
    );
  }

  // ── Logged in → full app (backend enforces plan limits) ──────
  return (
    <div style={{ height: '100vh', background: theme.bg, color: theme.textStrong }}>
      <SubscriptionBanner />
      <Routes>
        <Route path="/login" element={<Navigate to="/" replace />} />
        <Route path="/planes" element={<PlanesPage />} />
        <Route path="/onboarding" element={<OnboardingPage />} />
        <Route path="/" element={<SmartHome />} />
        <Route path="/registro-centro" element={<RegistroCentroPage />} />
        <Route element={<Layout />}>
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/trabajadores" element={<TrabajadoresPage />} />
          <Route path="/cuestionarios" element={<CuestionariosPage />} />
          <Route path="/cuestionarios/aplicar" element={<AplicarCuestionarioPage />} />
          <Route path="/consentimiento" element={<ConsentimientoPage />} />
          <Route path="/ficha/:trabajadorId" element={<FichaPage />} />
          <Route path="/resultados" element={<ResultadosPage />} />
          <Route path="/politica" element={<PoliticaPage />} />
          <Route path="/programa" element={<ProgramaPage />} />
          <Route path="/evidencia" element={<EvidenciaPage />} />
          <Route path="/enviar-links" element={<EnviarLinksPage />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
}
